import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ForgotPasswordForm, ResetPasswordForm } from "@lyder-talk/ui";
import { getAppUrl } from "@lyder-talk/core";
import { useTranslation } from "react-i18next";
import { auth } from "@lyder-talk/core/lib/auth-client";

/**
 * The landing page for a password-reset email.
 *
 * The link carries `?token_hash=…&type=recovery`. It used to be redeemed by
 * `consumeAuthEmailToken()`, which waited for a *recovery session* — but the
 * email was never sent (there was no mailer) and `verifyOtp` answered with a
 * stub error, so every link landed on "invalid". The token is now redeemed by
 * `auth.resetPassword()`, which needs no session: it verifies the token, writes
 * the new bcrypt hash and revokes the account's refresh tokens in one call.
 *
 * Keeping the token in the URL until submit is deliberate. It is single-use and
 * expires in an hour, and removing it before the request would make a retry
 * after a network blip impossible.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  // Accept the name our own mailer uses (`token_hash`, matching the old GoTrue
  // templates) and a bare `token` so an old or hand-built link still works.
  const token = searchParams.get("token_hash") ?? searchParams.get("token") ?? "";

  const handleSubmit = async (password: string) => {
    const { error } = await auth.resetPassword({ token, password });
    // The form renders `instanceof Error` messages; an AuthError is a plain
    // object, so passing it through would replace the server's "this link has
    // expired" with a generic failure.
    if (error) throw new Error(error.message);

    toast({
      title: t("auth.forms.reset.successTitle", "Password updated"),
      description: t(
        "auth.forms.reset.successDescription",
        "Your password has been reset. Sign in with your new password.",
      ),
    });
    // Replace, so the spent token does not stay in history.
    navigate("/auth/signin", { replace: true });
  };

  const handleResend = async (email: string) => {
    const { error } = await auth.resetPasswordForEmail(email, {
      redirectTo: getAppUrl("/auth/reset-password"),
    });
    if (error) throw new Error(error.message);
  };

  if (!token) {
    // No token at all: the link is malformed or was opened by hand. Offer a new
    // one instead of a form that could only fail.
    return (
      <div className="w-full">
        <div
          role="alert"
          className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          {t(
            "auth.forms.reset.missingToken",
            "This password reset link is missing its token. Request a new one below.",
          )}
        </div>
        <ForgotPasswordForm
          onSubmit={handleResend}
          onNavigateToSignin={() => navigate("/auth/signin")}
        />
      </div>
    );
  }

  return (
    <ResetPasswordForm
      onSubmit={handleSubmit}
      onNavigateToSignin={() => navigate("/auth/signin")}
      title={t("auth.forms.reset.title")}
      description={t("auth.forms.reset.description")}
      passwordLabel={t("auth.forms.reset.passwordLabel")}
      confirmPasswordLabel={t("auth.forms.reset.confirmPasswordLabel")}
      submitLabel={t("auth.forms.reset.submit")}
      submittingLabel={t("auth.forms.reset.submitting")}
      copy={{
        showPasswordLabel: t("auth.forms.reset.showPassword"),
        hidePasswordLabel: t("auth.forms.reset.hidePassword"),
        passwordStrengthLabels: [
          t("auth.passwordStrength.label", "Strength"),
          t("auth.passwordStrength.weak", "Weak"),
          t("auth.passwordStrength.fair", "Fair"),
          t("auth.passwordStrength.strong", "Strong"),
          t("auth.passwordStrength.excellent", "Excellent"),
        ],
        passwordRuleLabels: [
          t("auth.passwordRules.characters", "8+ characters"),
          t("auth.passwordRules.number", "a number"),
          t("auth.passwordRules.capital", "a capital letter"),
        ],
        matchHint: t("auth.forms.reset.matchHint"),
        backToSignIn: t("auth.forms.reset.backToSignIn"),
        tooShort: t("auth.forms.reset.tooShort"),
        passwordsMismatch: t("auth.forms.reset.passwordsMismatch"),
        failed: t("auth.forms.reset.failed"),
      }}
    />
  );
};

export default ResetPassword;
